import React, { useState } from 'react';

// 0=拉盘 1=砸盘 2=刷交易量 3=高抛低吸
export type ModeValue = 0 | 1 | 2 | 3;

interface ModeSelectorProps {
  value: ModeValue;
  onChange: (value: ModeValue) => void;
}

interface ModeItem {
  id: ModeValue;
  label: string;
  icon: React.ReactNode;
}

const PRIMARY = '#1fc7d4';
const PRIMARY_LIGHT = 'rgba(31, 199, 212, 0.08)';
const PRIMARY_BORDER = 'rgba(31, 199, 212, 0.5)';
const PRIMARY_SHADOW = 'rgba(31, 199, 212, 0.15)';

const ChartLineIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 17 9 11 13 15 21 7" />
    <polyline points="14 7 21 7 21 14" />
  </svg>
);

const ChartLineDownIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 7 9 13 13 9 21 17" />
    <polyline points="14 17 21 17 21 10" />
  </svg>
);

const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const ArrowsUpDownIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="7 3 7 21" />
    <polyline points="3 7 7 3 11 7" />
    <polyline points="17 21 17 3" />
    <polyline points="13 17 17 21 21 17" />
  </svg>
);

const modeItems: ModeItem[] = [
  { id: 0, label: '拉盘', icon: <ChartLineIcon /> },
  { id: 1, label: '砸盘', icon: <ChartLineDownIcon /> },
  { id: 2, label: '刷交易量', icon: <RefreshIcon /> },
  { id: 3, label: '高抛低吸', icon: <ArrowsUpDownIcon /> },
];

const ModeSelector: React.FC<ModeSelectorProps> = ({ value, onChange }) => {
  const [hovered, setHovered] = useState<ModeValue | null>(null);

  const containerStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
  };

  const getCardStyle = (id: ModeValue): React.CSSProperties => {
    const isActive = value === id;
    const isHovered = hovered === id && !isActive;

    return {
      borderRadius: '8px',
      padding: '12px',
      textAlign: 'center',
      cursor: 'pointer',
      backgroundColor: isActive ? PRIMARY_LIGHT : isHovered ? '#f7f8fa' : '#ffffff',
      border: isActive ? `1px solid ${PRIMARY_BORDER}` : '1px solid #e6e8eb',
      boxShadow: isActive ? `0 4px 12px ${PRIMARY_SHADOW}` : 'none',
      transition: 'all 0.2s ease',
      outline: 'none',
      userSelect: 'none',
    };
  };

  const getIconStyle = (id: ModeValue): React.CSSProperties => ({
    marginBottom: '4px',
    color: value === id ? PRIMARY : '#9aa3ad',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'color 0.2s ease',
  });

  const getLabelStyle = (id: ModeValue): React.CSSProperties => ({
    fontSize: '12px',
    fontWeight: 500,
    color: value === id ? PRIMARY : '#7a8590',
    transition: 'color 0.2s ease',
  });

  return (
    <div style={containerStyle}>
      {modeItems.map((mode) => {
        const isActive = value === mode.id;
        return (
          <div
            key={mode.id}
            style={getCardStyle(mode.id)}
            onClick={() => onChange(mode.id)}
            onMouseEnter={() => setHovered(mode.id)}
            onMouseLeave={() => setHovered(null)}
            role="button"
            aria-pressed={isActive}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange(mode.id);
              }
            }}
          >
            <div style={getIconStyle(mode.id)}>{mode.icon}</div>
            <div style={getLabelStyle(mode.id)}>{mode.label}</div>
          </div>
        );
      })}
    </div>
  );
};

export default ModeSelector;